/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { IEventModel } from "@bms/common";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CompetitorRow, IRow } from "./CompetitorRow";

import "./CompetitorsTab.scss";

type ICompetitor = NonNullable<IEventModel["Competitors"]>[number];

interface ICompetitorsTabProps {
  competitors: ICompetitor[];
  title?: string;
}

export const CompetitorsTab = ({
  competitors,
  title,
}: ICompetitorsTabProps) => {
  const { t } = useTranslation();

  const rows = useMemo<IRow[]>(() => {
    const sorted = [...competitors].sort((a, b) => {
      if (a.Position == null && b.Position == null) return 0;
      if (a.Position == null) return 1;
      if (b.Position == null) return -1;
      return a.Position - b.Position;
    });
    return sorted.map((c, index) => ({
      ...c,
      DisplayPosition: c.Position ?? index + 1,
    }));
  }, [competitors]);

  const hasPlayerTeam = rows.some((r) => r.Team?.Name);
  const hasScore = rows.some((r) => r.Score != null);
  const hasResultTime = rows.some((r) => r.ResultTime != null);

  return (
    <div className="CompetitorsTab">
      {title && <div className="CompetitorsTab__title">{title}</div>}
      <div className="CompetitorsTab__table-wrapper">
        <table className="CompetitorsTab__table">
          <thead>
            <tr>
              <th className="CompetitorsTab__col--pos">#</th>
              <th className="CompetitorsTab__col--competitor">
                {t("EVENT_COMPETITORS_COLUMN_PLAYER")}
              </th>
              {hasPlayerTeam && (
                <th className="CompetitorsTab__col--team">
                  {t("EVENT_COMPETITORS_COLUMN_TEAM")}
                </th>
              )}
              {hasResultTime && (
                <th className="CompetitorsTab__col--time">
                  {t("EVENT_COMPETITORS_COLUMN_TIME")}
                </th>
              )}
              {hasScore && (
                <th className="CompetitorsTab__col--score">
                  {t("EVENT_COMPETITORS_COLUMN_SCORE")}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <CompetitorRow
                key={row.Id}
                row={row}
                hasPlayerTeam={hasPlayerTeam}
                hasResultTime={hasResultTime}
                hasScore={hasScore}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
